/**
 * Transcript chunk lookup filter helpers — map UI state to Brooklyn `TranscriptChunkSearchFilter`
 * and `contentList` typeahead queries (ported from gaia-web-app semantic-stitch filters + person scope).
 */

export const FILTER_SCOPE_ANY = 'ANY'
export const FILTER_SCOPE_VIDEO = 'VIDEO'
export const FILTER_SCOPE_SERIES = 'SERIES'
export const FILTER_SCOPE_CENTER = 'CENTER'
export const FILTER_SCOPE_PERSON = 'PERSON'

export const FILTER_SCOPE_OPTIONS = [
  { value: FILTER_SCOPE_ANY, label: 'Any content' },
  { value: FILTER_SCOPE_VIDEO, label: 'Videos' },
  { value: FILTER_SCOPE_SERIES, label: 'A series' },
  { value: FILTER_SCOPE_CENTER, label: 'A center' },
  { value: FILTER_SCOPE_PERSON, label: 'A person' },
] as const

export const PUBLISHED_AGE_OPTIONS = [
  { value: 'ANY', label: 'Any publish date' },
  { value: 'PAST_MONTH', label: 'Past month' },
  { value: 'PAST_3_MONTHS', label: 'Past 3 months' },
  { value: 'PAST_6_MONTHS', label: 'Past 6 months' },
  { value: 'PAST_YEAR', label: 'Past year' },
  { value: 'PAST_2_YEARS', label: 'Past 2 years' },
  { value: 'PAST_3_YEARS', label: 'Past 3 years' },
  { value: 'PAST_5_YEARS', label: 'Past 5 years' },
] as const

export const MAX_SEASONS = 20
export const TYPEAHEAD_DEBOUNCE_MS = 300
export const TYPEAHEAD_LIMIT = 50
/** Matches Brooklyn `MAX_VIDEO_LIST_FILTER_SIZE`. */
export const MAX_VIDEO_LIST_FILTER_SIZE = 100

export type FilterScope = (typeof FILTER_SCOPE_OPTIONS)[number]['value']
export type PublishedAge = (typeof PUBLISHED_AGE_OPTIONS)[number]['value']

export type TypeaheadItem = {
  contentId: number
  title?: string | null
  contentType?: string | null
  bundle?: string | null
}

/** Parse pasted tracker text into deduped positive catalog content IDs. */
export function parseContentIdsFromText (text: string): number[] {
  const ids = text
    .split(/[\s,]+/u)
    .map((part) => parseInt(part.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0)
  return [...new Set(ids)]
}

export function mergeVideoItemsByContentId (
  existing: TypeaheadItem[],
  contentIds: number[],
): TypeaheadItem[] {
  const byId = new Map(existing.map((item) => [item.contentId, item]))
  contentIds.forEach((contentId) => {
    if (!byId.has(contentId)) {
      byId.set(contentId, { contentId, title: `#${contentId}` })
    }
  })
  return [...byId.values()]
}

export function isVideoListOverCap (items: TypeaheadItem[]): boolean {
  return items.length > MAX_VIDEO_LIST_FILTER_SIZE
}

export function effectiveVideoFilterItems (
  filterVideoItems: TypeaheadItem[],
  pasteIdsText: string,
): TypeaheadItem[] {
  const pendingIds = parseContentIdsFromText(pasteIdsText)
  if (pendingIds.length === 0) {
    return filterVideoItems
  }
  return mergeVideoItemsByContentId(filterVideoItems, pendingIds)
}

export function isFilterScopeIncomplete ({
  filterScope,
  filterItem,
  filterVideoItems = [],
}: {
  filterScope: FilterScope
  filterItem: TypeaheadItem | null
  filterVideoItems?: TypeaheadItem[]
}): boolean {
  if (filterScope === FILTER_SCOPE_ANY) {
    return false
  }
  if (filterScope === FILTER_SCOPE_VIDEO) {
    return filterVideoItems.length === 0
  }
  return filterItem == null
}

export function hasActiveFilterState ({
  filterScope,
  filterItem,
  filterVideoItems = [],
  filterPublishedAge,
}: {
  filterScope: FilterScope
  filterItem: TypeaheadItem | null
  filterVideoItems?: TypeaheadItem[]
  filterPublishedAge: PublishedAge
}): boolean {
  const hasVideoScope = filterScope === FILTER_SCOPE_VIDEO && filterVideoItems.length > 0
  const hasOtherScope =
    filterScope !== FILTER_SCOPE_ANY && filterScope !== FILTER_SCOPE_VIDEO && filterItem != null
  return hasVideoScope || hasOtherScope || filterPublishedAge !== 'ANY'
}

export function buildSearchFilter ({
  filterScope,
  filterItem,
  filterVideoItems,
  filterSeason,
  filterPublishedAge,
}: {
  filterScope: FilterScope
  filterItem: TypeaheadItem | null
  filterVideoItems: TypeaheadItem[]
  filterSeason: number | null
  filterPublishedAge: PublishedAge
}) {
  const hasVideoScope = filterScope === FILTER_SCOPE_VIDEO && filterVideoItems.length > 0
  const hasOtherScope =
    filterScope !== FILTER_SCOPE_ANY && filterScope !== FILTER_SCOPE_VIDEO && filterItem != null
  const hasDate = filterPublishedAge !== 'ANY'

  if (!hasVideoScope && !hasOtherScope && !hasDate) {
    return null
  }

  const base = hasDate ? { publishedAge: filterPublishedAge } : {}

  if (filterScope === FILTER_SCOPE_VIDEO && filterVideoItems.length > 0) {
    return {
      ...base,
      contentIds: filterVideoItems.map((item) => item.contentId),
    }
  }
  if (filterScope === FILTER_SCOPE_SERIES) {
    return {
      ...base,
      seriesContentId: filterItem!.contentId,
      seasonNumber: filterSeason != null ? filterSeason : undefined,
    }
  }
  if (filterScope === FILTER_SCOPE_CENTER) {
    return { ...base, centerContentId: filterItem!.contentId }
  }
  if (filterScope === FILTER_SCOPE_PERSON) {
    return { ...base, personContentId: filterItem!.contentId }
  }
  return hasDate ? base : null
}

export function buildTypeaheadWhere (scope: FilterScope, term: string) {
  const titleFilter = term
    ? { title: { ilike: `%${term.split(/\s+/u).join('%')}%` } }
    : { title: { ilike: '%' } }

  if (scope === FILTER_SCOPE_VIDEO) {
    return { ...titleFilter, contentType: { eq: 'video' } }
  }
  if (scope === FILTER_SCOPE_SERIES) {
    return { ...titleFilter, contentType: { eq: 'series' } }
  }
  if (scope === FILTER_SCOPE_CENTER) {
    return { ...titleFilter, contentType: { eq: 'tag' }, bundle: { eq: 'centers' } }
  }
  if (scope === FILTER_SCOPE_PERSON) {
    return { ...titleFilter, contentType: { eq: 'person' }, bundle: { eq: 'people' } }
  }
  return null
}
